/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from 'react';
import { components } from 'generated/payment-initialisation/schema';
import t from 'utils/translationHelper';

import { IStyledGenericTableProps } from '@alpha/ui-lib/ui/table';
import { APTooltip } from '@alpha/ui-lib/ui/Tooltip';
import { faCircleExclamation, faExclamationCircle, faPencil } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import RemittersActions from './RemittersActions';

export const remittersColumns: IStyledGenericTableProps['columns'] = [
  {
    header: '',
    accessor: 'statusIndicator',
    width: 24,
  },
  {
    header: t('remitter_name'),
    accessor: 'Payee',
  },
  {
    header: t('party'),
    accessor: 'ThirdParty',
  },
  {
    header: t('country'),
    accessor: 'PayeeCountryCode',
  },
  {
    header: t('currency'),
    accessor: 'Currency',
  },
  {
    header: t('bank_name'),
    accessor: 'BankName',
  },
  {
    header: t('iban'),
    accessor: 'Iban',
  },
  {
    header: t('swift/bic'),
    accessor: 'Bic',
  },
  {
    header: t('bank_address'),
    accessor: 'BankUnstructuredAddress',
  },
  {
    header: t('actions'),
    accessor: 'Actions',
    width: 70,
  },
];

export interface RemitterRowData {
  [key: string]: string | React.ReactNode | boolean;
  requiresAction: boolean;
}

const ACTION_REQUIRED_COLOR = '#CF752F';

const ActionRequiredIndicator: React.FC = () => (
  <APTooltip
    title={t('this_remitter_has_missing_data')}
    arrow
  >
    <div style={{
      width: '14px', display: 'inline-block', marginRight: '8px', color: '#CF752F',
    }}
    >
      <FontAwesomeIcon icon={faExclamationCircle} />
    </div>
  </APTooltip>
);

const StyledCell: React.FC<{ children: React.ReactNode; requiresAction: boolean }> = ({
  children,
  requiresAction,
}) => (
  <span style={requiresAction ? { color: ACTION_REQUIRED_COLOR } : undefined}>
    {children === '' || children === null || children === undefined ? '-' : children}
  </span>
);

export const remittersDataMapper = (
  data: components['schemas']['RemittersListResponse']['Remitters'],
  setOpenEditRemitterDrawer: (id?: string) => void,
): RemitterRowData[] => {
  if (!data) return [];

  return data.map((remitter) => {
    const fields = remitter.Fields || {};
    const id = remitter.Id;

    // Extract field values from the Fields object
    const fieldValues = Object.fromEntries(
      Object.entries(fields).map(([key, field]) => [key, field.Value]),
    );

    // Remitter requires action if it's not complete
    const requiresAction = remitter.Complete === false;

    // Format the ThirdParty display value
    const thirdPartyDisplay = fieldValues.ThirdParty === 'True'
      ? t('3party')
      : t('1party');

    // Helper to wrap cell content with styling
    const wrapCell = (content: string | null | undefined) => (
      <StyledCell requiresAction={requiresAction}>
        {content !== undefined && content !== null && content !== '' ? (
          <>
            {content.length > 45 ? (
              <APTooltip title={content as string}>
                <span>{`${content.substring(0, 45)}...`}</span>
              </APTooltip>
            ) : (
              <span>{content}</span>
            )}
          </>
        ) : (
          '-'
        )}
      </StyledCell>
    );

    return {
      Id: id,
      statusIndicator: requiresAction ? <ActionRequiredIndicator /> : null,
      Payee: wrapCell(fieldValues.Payee),
      ThirdParty: wrapCell(thirdPartyDisplay),
      PayeeCountryCode: wrapCell(fieldValues.PayeeCountryCode),
      Currency: wrapCell(fieldValues.Currency),
      BankName: wrapCell(fieldValues.BankName),
      Iban: wrapCell(fieldValues.Iban),
      Bic: wrapCell(fieldValues.Bic),
      BankUnstructuredAddress: wrapCell(fieldValues.BankUnstructuredAddress),
      requiresAction,
      Actions: (
        <RemittersActions
          menuItems={[
            {
              icon: faPencil,
              text: 'edit_details',
              action: () => setOpenEditRemitterDrawer(id),
            },
          ]}
        />
      ),
    };
  });
};
